import {
  ArrowRight,
  Briefcase,
  CircleTick,
  DocumentAdd,
  DoubleChevronDown,
  DoubleChevronUp,
  EditLinear,
  FilledNote,
} from '../icons';
import InteractiveCard from '../common/interactiveCard';
import { useTranslation } from 'react-i18next';
import CardData from '../dashboard/cardData';
import { ITask } from '../../interface/tasks';
import { getMatterIconClass, getMatterLabel } from '../../constants/utilityFunctions';
import { useLanguage } from '../../context/LanguageContext';
import {
  cardBorderColorMapping,
  GENERAL_ENTITY_DISABLED_ID,
  tagColorMapping,
  TasksPriority,
} from '../../constants/tasks';
import { ROUTES } from '../../constants/constants';
import { ITransaction } from '../../interface/transaction';
import { useTask } from '../../context/TaskContext';
import { Flex } from 'antd';
import { Divider, IconButton, Tooltip } from '../Ant';
import { useNavigationStack } from '../../context/NavigationStackContext';
import NoteIcons from '../common/NoteIcons';

const TaskCard = ({
  task: { activity, currentOfTotalDays },
  completeTask,
  handleRestrictions,
}: {
  task: { activity: ITask; currentOfTotalDays: string };
  completeTask?: (task?: ITask) => void;
  handleRestrictions: (task: ITask, transaction: ITransaction) => void;
}) => {
  const { push } = useNavigationStack();
  const { t } = useTranslation();
  const { getAppLanguageCode } = useLanguage();
  const { updateTask, clearInitialTask } = useTask();
  const translatedStatus =
    activity?.status?.translations?.find((t) => t.language === getAppLanguageCode())?.translation ??
    '-';
  const handleEditTask = (e: React.MouseEvent<HTMLButtonElement>) => {
    e?.stopPropagation();
    updateTask(null);
    clearInitialTask();
    push(ROUTES.ADD_EDIT_TASK + '/' + activity.id);
  };
  const handleGoToDetail = () => {
    push(ROUTES.TASKS_DETAIL + '/' + activity.id);
  };
  return (
    <InteractiveCard
      data-testid="task-card"
      className={`w-full task-card pointer ${cardBorderColorMapping[activity?.statusId] || cardBorderColorMapping.default}`}
      onActivate={handleGoToDetail}
    >
      <div className="d-flex justify-between pb-10 items-center">
        <div className="d-flex gap-5 items-center">
          <Flex
            data-testid="task-card-status"
            justify="center"
            align="center"
            className={`status-chip ${tagColorMapping[activity?.statusId] || tagColorMapping.default}`}
          >
            <span className="task-status-chip">{translatedStatus}</span>
          </Flex>
          <NoteIcons
            hasImportantNotes={activity?.hasImportantNotes}
            hasNotes={activity?.hasNotes}
          />
        </div>
        <Flex className="responsive-spacing">
          {activity?.statusId !== GENERAL_ENTITY_DISABLED_ID && completeTask && (
            <Tooltip title={t('task:markAsComplete')} placement="top" arrow={false}>
              <IconButton
                aria-label={t('task:markAsComplete')}
                data-testid="task-card-complete"
                icon={<CircleTick />}
                onClick={(e) => {
                  e?.stopPropagation();
                  completeTask?.(activity);
                }}
              />
            </Tooltip>
          )}
          {!activity?.transactionId && activity?.statusId !== GENERAL_ENTITY_DISABLED_ID && (
            <Tooltip title={t('task:taskToTransaction')} placement="top" arrow={false}>
              <IconButton
                aria-label={t('task:taskToTransaction')}
                data-testid="task-card-transaction"
                icon={<DocumentAdd />}
                onClick={(e) => {
                  e?.stopPropagation();
                  handleRestrictions(activity, {
                    matter: activity?.matter,
                    matterId: activity?.matterId,
                  } as ITransaction);
                }}
              />
            </Tooltip>
          )}
          {activity?.statusId !== GENERAL_ENTITY_DISABLED_ID && (
            <Tooltip title={t('task:edit')} placement="top" arrow={false}>
              <IconButton
                aria-label={t('task:edit')}
                data-testid="task-card-edit"
                icon={<EditLinear />}
                onClick={handleEditTask}
              />
            </Tooltip>
          )}
          <Tooltip title={t('transaction:goDetail')} placement="top" arrow={false}>
            <IconButton
              aria-label={t('transaction:goDetail')}
              data-testid="task-card-detail"
              icon={<ArrowRight />}
              onClick={handleGoToDetail}
            />
          </Tooltip>
        </Flex>
      </div>
      <Divider className="mb-10" />
      <div className="d-flex flex-col gap-10">
        <div className="d-flex justify-between">
          <CardData
            type="task"
            icon={<FilledNote />}
            title={t('task:task')}
            content={
              currentOfTotalDays.length > 0
                ? `(${t('common:day')} ${currentOfTotalDays}) ${activity.subject}`
                : '' + activity.subject
            }
            contentclass="lineclamp-1"
          />
          {activity.priority === TasksPriority.high && (
            <DoubleChevronUp data-testid="task-card-priority-high" className="opacity-50" />
          )}
          {activity.priority === TasksPriority.low && (
            <DoubleChevronDown data-testid="task-card-priority-low" className="opacity-50" />
          )}
        </div>
        {activity?.matter && (
          <CardData
            type="matter"
            icon={
              <Briefcase className={getMatterIconClass(activity?.matter?.invoiceableType ?? '')} />
            }
            title={getMatterLabel(t, activity?.matter?.invoiceableType)}
            content={
              activity?.matter?.principalCompany?.name
                ? activity?.matter?.principalCompany?.name
                : (activity?.matter?.principalPrivatePerson?.fullName ?? '')
            }
            contentclass="lineclamp-1"
            subcontentclass="lineclamp-1"
            subcontent={activity?.matter?.matterNumber + ' - ' + activity?.matter?.subject}
          />
        )}
      </div>
    </InteractiveCard>
  );
};

export default TaskCard;
